import { DatePipe } from '@angular/common';
import { FormatNumberPipe } from '../../../../../shared/pipes/number-separator.pipe';
import { EntityPopup, PopupInfoItem } from './entity-popup.interface';

/**
 * Clase base abstracta para los popups de estaciones de sensores.
 * Proporciona métodos utilitarios para crear items de información
 * a partir de los datos de sensores.
 *
 * @example
 * ```typescript
 * class MiEstacionPopup extends EstacionPopup {
 *   getInfoItems(): PopupInfoItem[] {
 *     return [
 *       this.createSensorItem(this.entity.temperatura_last, 'Temperatura', 'device_thermostat', '°C'),
 *     ];
 *   }
 * }
 * ```
 */
export abstract class EstacionPopup implements EntityPopup {
  protected datePipe = new DatePipe('es-ES');
  protected formatNumber = new FormatNumberPipe();

  constructor(protected entity: any) {}

  abstract getBadgeText(): string;
  abstract getInfoItems(): PopupInfoItem[];

  /**
   * Crea un PopupInfoItem a partir de los datos de un sensor.
   * Si el sensor no tiene datos válidos, devuelve un item con value: null.
   *
   * @param sensor - Objeto del sensor con propiedades value y fecha_timestamp
   * @param label - Etiqueta para mostrar
   * @param icon - Nombre del icono de Material Symbols
   * @param unit - Unidad de medida opcional
   */
  protected createSensorItem(
    sensor:
      | { value?: number; label?: string; fecha_timestamp?: string }
      | null
      | undefined,
    label: string,
    icon: string,
    unit?: string
  ): PopupInfoItem {
    if (!sensor || sensor.value === null || sensor.value === undefined) {
      return { icon, label, value: null };
    }

    const date = sensor.fecha_timestamp
      ? (this.datePipe.transform(
          sensor.fecha_timestamp,
          'dd/MM/yyyy HH:mm:ss'
        ) ?? undefined)
      : undefined;

    return {
      icon,
      label: sensor.label || label,
      value: sensor.value,
      unit,
      date,
    };
  }
}
